/**
 * CR168: Add Alias from Discovery Dialog
 * Quick flow to add a merchant alias for unmatched flyers
 */

import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useMerchantAliases } from "@/hooks/useMerchantAliases";
import type { DiscoveredFlyer } from "@/lib/flyer-bucket/types";

interface AddAliasFromDiscoveryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  flyer: DiscoveredFlyer | null;
  onSuccess?: () => void;
}

export function AddAliasFromDiscoveryDialog({
  open,
  onOpenChange,
  flyer,
  onSuccess,
}: AddAliasFromDiscoveryDialogProps) {
  const { createAlias } = useMerchantAliases();
  const [saving, setSaving] = useState(false);
  const [merchantId, setMerchantId] = useState("");
  const [aliasValue, setAliasValue] = useState("");

  // Load merchants for selection
  const { data: merchants = [] } = useQuery({
    queryKey: ["merchants-for-alias"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("merchants")
        .select("merchant_key, display_name")
        .order("display_name");
      if (error) throw error;
      return data || [];
    },
  });

  useEffect(() => {
    if (open && flyer) {
      setAliasValue(flyer.merchant_key_raw);
      setMerchantId("");
    }
  }, [open, flyer]);

  const handleSave = async () => {
    if (!merchantId || !aliasValue.trim()) return;

    setSaving(true);
    try {
      const success = await createAlias(merchantId, aliasValue.trim(), "filename", 0);
      if (success) {
        onOpenChange(false);
        onSuccess?.();
      }
    } finally {
      setSaving(false);
    }
  };

  if (!flyer) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add Merchant Alias</DialogTitle>
          <DialogDescription>
            Create a filename alias for "{flyer.merchant_key_raw}" to enable automatic matching
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Alias Value</Label>
            <Input
              value={aliasValue}
              onChange={(e) => setAliasValue(e.target.value)}
              placeholder="e.g., Walmart"
            />
            <p className="text-xs text-muted-foreground">
              This is the raw merchant name from the filename
            </p>
          </div>

          <div className="space-y-2">
            <Label>Map to Merchant *</Label>
            <Select value={merchantId} onValueChange={setMerchantId}>
              <SelectTrigger>
                <SelectValue placeholder="Select a merchant..." />
              </SelectTrigger>
              <SelectContent>
                {merchants.map((m) => (
                  <SelectItem key={m.merchant_key} value={m.merchant_key}>
                    {m.display_name || m.merchant_key}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Which merchant should this alias resolve to?
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || !merchantId || !aliasValue.trim()}
          >
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Add Alias
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
